// src/components/common/actionBeat/ActionBeatFill.js
//
// The FILL footer: owns a confirm's whole button row in every phase (idle →
// working → success) so Cancel can fold away and the tapped button itself
// fills, with progress right where the finger is. Visual reference:
// docs/mockups/action-beats-system.html "FILL (A)" CSS.
//
// The fill overlay + stacked labels + drawn check live in FillButtonContent
// (extracted WAVE 1 · ENGINE, 2026-09-24) — ConfirmMorphActions reuses that
// exact visual once the two-tap capsule itself starts filling on commit.
// This file keeps the PHASE/STAGE state machine that is specific to a
// component that can mount directly at phase="working" (a drawer swapping
// its own idle footer for this one after saving already started).

import { useEffect, useRef, useState } from 'react';
import { foundation } from '../../../ui';
import { overlayFooterButtonStyle } from '../../../ui/overlayFooter';
import { FILL_MS, BEAT_EASE, touchActionBoxFloor, ACTION_BEAT_ATTR } from '../../../ui/actionBeatMotion';
import { useResponsiveLayout } from '../../../hooks/ui/useResponsiveLayout';
import FillButtonContent, { finishStyle } from './FillButtonContent';

const { spacePx } = foundation;

export default function ActionBeatFill({
  phase, tone, buttonTone = 'primary', confirmLabel, cancelLabel, onConfirm, onCancel,
  canConfirm = true, busy = false, workingLabel, successLabel, reduced = false,
  confirmTestId = 'confirm-dialog-confirm-button', cancelTestId = 'confirm-dialog-cancel-button',
  rowStyle, confirmStyle, cancelStyle,
  // Idle column split for the Cancel/confirm pair. Must stay fr-based so the
  // fold into '0fr 1fr' still tweens (KAN-696: drawer footers use '1fr 2fr').
  idleColumns = '1fr 1fr',
  // Opt-in: ellipsize the labels inside a fixed-width button (default off).
  // `'clip'` = same boxes, label centred and clipped at both ends, no "..."
  // (a button shrinking back after a beat).
  ellipsize = false,
  // Opt-in (KAN-772): the outgoing label fades fully out before the incoming one
  // fades in (never two legible labels at once). Default off — every other consumer unchanged.
  sequentialLabels = false,
}) {
  // SIZE only (KAN-970): the 44px floor follows touch, not width.
  const { isTouch: touch } = useResponsiveLayout();

  // Mount-in-'working' consumers (a drawer swapping its own idle footer for
  // this one once saving starts) still need to SEE the fold + sweep animate,
  // not snap straight to the end state — so the first paint is always idle
  // geometry, then (barring reduced motion) a double rAF applies the real
  // requested phase.
  const mountedRef = useRef(false);
  const [displayPhase, setDisplayPhase] = useState(() => (reduced ? phase : 'idle'));
  useEffect(() => {
    if (mountedRef.current) { setDisplayPhase(phase); return undefined; }
    mountedRef.current = true;
    if (reduced || phase === 'idle') { setDisplayPhase(phase); return undefined; }
    let raf2;
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => setDisplayPhase(phase)); });
    return () => { cancelAnimationFrame(raf1); if (raf2) cancelAnimationFrame(raf2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const [stage, setStage] = useState('start');
  useEffect(() => {
    // Back at rest (a failed save returns to idle): clear the sweep so no half-filled strip is left behind.
    if (displayPhase === 'idle') { setStage('start'); return undefined; }
    if (reduced) { setStage('run'); return undefined; }
    if (displayPhase !== 'working') return undefined;
    setStage('run');
    const t = setTimeout(() => setStage('crawl'), FILL_MS.crawlAfter);
    return () => clearTimeout(t);
  }, [displayPhase, reduced]);

  const activeBeat = displayPhase !== 'idle';
  const success = displayPhase === 'success';
  const idleDisabled = !canConfirm || busy;
  // The REAL phase locks the buttons the moment the beat starts; displayPhase only trails it by two frames.
  const beatLocked = activeBeat || (Boolean(phase) && phase !== 'idle');
  const confirmDisabled = beatLocked || idleDisabled;

  // The row is a grid INSIDE the dialog's flex actions row: without an
  // explicit full width it shrink-wraps to its labels and the 1fr/1fr pair
  // stops short of the right edge (seen on device, 2026-09-25). Legacy
  // confirm buttons fill the row via `flex: 1 1 auto` — match that.
  const finalRowStyle = {
    width: '100%',
    flex: '1 1 100%',
    minWidth: 0,
    ...(rowStyle || {}),
    display: 'grid',
    gridTemplateColumns: cancelLabel ? (activeBeat ? '0fr 1fr' : idleColumns) : '1fr',
    gap: cancelLabel && activeBeat ? 0 : (cancelLabel ? spacePx[3] : 0),
    transition: reduced ? 'none' : `grid-template-columns 260ms ${BEAT_EASE.out}, gap 260ms ${BEAT_EASE.out}`,
  };

  // The ONE footer button (KAN-948): 38px web / 44px touch. A caller's confirmStyle / cancelStyle
  // (the same overlayFooter styles its idle row uses) lays over these, so the box never changes.
  const toneBase = overlayFooterButtonStyle({ isTouch: touch, kind: buttonTone === 'danger' ? 'danger' : 'primary' });
  // ONE BOX through every phase (KAN-768): only colour/opacity/cursor change
  // between idle -> working -> success. Height, padding, border width and
  // box-sizing are the same object in all three (the drawn check has a 0-tall
  // margin box, FillButtonContent), so the button never grows when the check
  // or a longer working label arrives. Guard: ActionBeatFill.sameBox.test.js.
  const confirmFinal = {
    ...toneBase,
    ...(confirmStyle || {}),
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
    ...touchActionBoxFloor(touch, confirmStyle?.minHeight),
    ...(success ? finishStyle(tone) : (idleDisabled && !activeBeat ? { opacity: 'var(--cvx-fill-disabled-opacity, 0.45)', cursor: 'not-allowed' } : {})),
    cursor: confirmDisabled ? (activeBeat ? 'default' : 'not-allowed') : 'pointer',
  };
  const cancelFinal = {
    ...overlayFooterButtonStyle({ isTouch: touch, kind: 'cancel' }),
    ...(cancelStyle || {}),
    ...touchActionBoxFloor(touch, cancelStyle?.minHeight),
    ...(activeBeat ? { opacity: 0, paddingLeft: 0, paddingRight: 0, borderWidth: 0, minWidth: 0, overflow: 'hidden', pointerEvents: 'none' } : {}),
    transition: reduced ? 'none' : `opacity 200ms ease, padding 260ms ${BEAT_EASE.out}, border-width 260ms ${BEAT_EASE.out}`,
  };

  return (
    <div style={finalRowStyle} data-beat-kind="fill" data-fill-phase={displayPhase} data-testid={phase !== 'idle' ? 'modal-action-beat' : undefined}
      {...(phase !== 'idle' ? { [ACTION_BEAT_ATTR]: phase } : null)}>
      {cancelLabel ? (
        <button type="button" data-testid={cancelTestId} style={cancelFinal} disabled={beatLocked}
          aria-hidden={activeBeat || undefined} tabIndex={activeBeat ? -1 : undefined}
          onClick={beatLocked ? undefined : onCancel}>
          {cancelLabel}
        </button>
      ) : null}
      <button type="button" data-testid={confirmTestId} style={confirmFinal} disabled={confirmDisabled}
        {...(idleDisabled && !activeBeat && !success ? { 'data-cvx-fill-idle-disabled': '' } : null)}
        onClick={confirmDisabled ? undefined : onConfirm}>
        <FillButtonContent
          phase={displayPhase}
          stage={stage}
          reduced={reduced}
          confirmLabel={confirmLabel}
          workingLabel={workingLabel}
          successLabel={successLabel}
          ellipsize={ellipsize}
          sequentialLabels={sequentialLabels}
        />
      </button>
    </div>
  );
}
